// colDef/studentColDef.tsx
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PlacementStatus, StudentType } from "@/services/student.service";
import { createColumnHelper } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const columnHelper = createColumnHelper<StudentType>();

export type StudentActionType = {
  id: string;
  name: string;
  action: "edit" | "delete";
};

// ─── Placement pill config ────────────────────────────────────────────────────

const PLACEMENT_CONFIG: Record<
  PlacementStatus,
  { label: string; className: string }
> = {
  placed: {
    label: "Placed",
    className: "bg-gray-900 text-white",
  },
  pending: {
    label: "Pending",
    className: "bg-gray-100 text-gray-600",
  },
  "n/a": {
    label: "N/A",
    className: "text-gray-400",
  },
};

export const createStudentColumns = (
  onActionClick: (payload: StudentActionType) => void
) => [
  // Roll No — link to view page
  columnHelper.accessor("rollNo", {
    header: "Roll No",
    cell: (info) => (
      <Link
        to={`/students/${info.row.original._id}`}
        className="font-medium hover:underline"
      >
        {info.getValue()}
      </Link>
    ),
  }),

  // Name
  columnHelper.accessor("name", {
    header: "Name",
    cell: (info) => <span className="font-medium">{info.getValue()}</span>,
  }),

  // Email
  columnHelper.accessor("email", {
    header: "Email",
    cell: (info) => {
      const email = info.getValue();
      return (
        <span className="text-sm">
          {email ? email : <span className="text-gray-400">—</span>}
        </span>
      );
    },
  }),

  // Phone / Mobile
  columnHelper.accessor("phone", {
    header: "Mobile",
    cell: (info) => {
      const phone = info.getValue();
      return (
        <span className="text-sm">
          {phone ? phone : <span className="text-gray-400">—</span>}
        </span>
      );
    },
  }),

  // Dept — dark badge pill
  columnHelper.accessor("dept", {
    header: "Dept",
    cell: (info) => (
      <span className="rounded-md bg-gray-900 px-2.5 py-1 text-xs font-bold text-white">
        {info.getValue()}
      </span>
    ),
  }),

  // Year
  columnHelper.accessor("year", {
    header: "Year",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // CGPA — blue
  columnHelper.accessor("cgpa", {
    header: "CGPA",
    cell: (info) => (
      <span className="font-semibold text-blue-600">{info.getValue()}</span>
    ),
  }),

  // Internship — plain
  columnHelper.accessor("internship", {
    header: "Internship",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Placement pill
  columnHelper.accessor("placement", {
    header: "Placement",
    cell: (info) => {
      const cfg = PLACEMENT_CONFIG[info.getValue()];
      if (info.getValue() === "n/a") {
        return (
          <span className={cn("text-sm", cfg.className)}>{cfg.label}</span>
        );
      }
      return (
        <span
          className={cn(
            "rounded-full px-3 py-1 text-xs font-semibold",
            cfg.className
          )}
        >
          {cfg.label}
        </span>
      );
    },
  }),

  // Status pill
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const isActive = info.getValue() === "active";
      return (
        <span
          className={cn(
            "rounded-full px-3 py-1 text-xs font-semibold",
            isActive ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-500"
          )}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
      );
    },
  }),

  // Inline edit / delete
  columnHelper.display({
    id: "actions",
    header: "Actions",
    cell: (info) => {
      const data = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({ id: data._id, name: data.name, action: "edit" })
            }
            aria-label="Edit student"
          >
            <Pencil size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({ id: data._id, name: data.name, action: "delete" })
            }
            aria-label="Delete student"
          >
            <Trash2 size={15} />
          </Button>
        </div>
      );
    },
  }),
];
